import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../lib/supabase';
import { getCachedLockedCountries, getDeviceId } from '../services/votes';

const AuthContext = createContext();

function mapUser(supabaseUser) {
  if (!supabaseUser) return null;
  return {
    id: supabaseUser.id,
    email: supabaseUser.email,
  };
}

async function linkDeviceIdentity(userId) {
  const { error } = await supabase.rpc('link_identity_to_user', {
    p_ip_hash: getDeviceId(),
    p_user_id: userId,
  });
  if (error) {
    console.warn('link_identity_to_user failed:', error.message);
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lockedCountries, setLockedCountries] = useState(() => getCachedLockedCountries());

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(mapUser(session?.user ?? null));
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(mapUser(session?.user ?? null));
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
    if (data.user) {
      await linkDeviceIdentity(data.user.id);
    }
    return data;
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  /** Update lock display from cast_vote server response (not client-side guessing). */
  const syncLockedCountriesFromVote = (lockedCountry1, lockedCountry2) => {
    const countries = [lockedCountry1, lockedCountry2].filter(Boolean);
    setLockedCountries(countries);
  };

  const resetLockedCountries = () => {
    if (user) {
      setLockedCountries([]);
      return true;
    }
    return false;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      signUp,
      signIn,
      signOut,
      logout: signOut,
      lockedCountries,
      syncLockedCountriesFromVote,
      resetLockedCountries,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
